import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'app/rootReducer'
import { Formik, Field, Form, FormikHelpers, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Button from 'react-bootstrap/Button'
import {
  CreatePasswordValidationType,
  ConfirmPasswordValidationType,
  confirmPasswordValidation,
  createPasswordValidation,
  getPasswordRulesPopover,
  createPasswordValidationTypeInitial,
  confirmPasswordValidationTypeInitial,
  CurrentPasswordValidationType
} from 'share/utils/passwordUtils'
import {
  ChangePasswordVariables,
  ChangePasswordResponseData
} from 'share/mutations/account'
import './styles.scss'

interface Props {
  placement: 'right' | 'top'
  currentPasswodValidationResult: CurrentPasswordValidationType
  setCurrentPasswodValidationResult: React.Dispatch<
    React.SetStateAction<CurrentPasswordValidationType>
  >
  createPasswordValidationResult: CreatePasswordValidationType
  setCreatePasswordValidationResult: React.Dispatch<
    React.SetStateAction<CreatePasswordValidationType>
  >
  confirmPasswordValidationResult: ConfirmPasswordValidationType
  setConfirmPasswordValidationResult: React.Dispatch<
    React.SetStateAction<ConfirmPasswordValidationType>
  >
  nextPage(page: string): void
  changePasswordHelper(
    input: ChangePasswordVariables
  ): Promise<ChangePasswordResponseData>
}

interface Values {
  currentPassword: string
  createPassword: string
  confirmPassword: string
}

const validationSchema = Yup.object({
  currentPassword: Yup.string().required('Required'),
  createPassword: Yup.string().required('Required'),
  confirmPassword: Yup.string().required('Required')
})

let popover: JSX.Element = <></>
const createPwdInitial = {
  ...createPasswordValidationTypeInitial(),
  isValid: true
}
const confirmPwdInitial = {
  ...confirmPasswordValidationTypeInitial(),
  isValid: true
}

const UserProfileResetPasswordForm = (props: Props): JSX.Element => {
  const {
    placement,
    currentPasswodValidationResult,
    setCurrentPasswodValidationResult,
    createPasswordValidationResult,
    setCreatePasswordValidationResult,
    confirmPasswordValidationResult,
    setConfirmPasswordValidationResult,
    changePasswordHelper,
    nextPage
  } = props
  const authentication = useSelector((state: RootState) => state.authentication)

  const [popoverValidation, setPopoverValidation] = useState(() =>
    createPasswordValidationTypeInitial()
  )

  const initialValues = {
    currentPassword: '',
    createPassword: '',
    confirmPassword: ''
  }

  const handlePasswordInput = (password: string) => {
    setCreatePasswordValidationResult(createPwdInitial)
    const result = createPasswordValidation(password)
    setPopoverValidation(result)
  }

  useEffect(() => {
    popover = getPasswordRulesPopover(popoverValidation)
  }, [popoverValidation])

  const handleSubmitPasswordCheck = (
    password: string,
    confirmPassword: string
  ): [CreatePasswordValidationType, ConfirmPasswordValidationType] => {
    let createPwdResult = createPasswordValidationTypeInitial()
    let confirmPwdResult = confirmPasswordValidationTypeInitial()

    createPwdResult = createPasswordValidation(password)
    if (confirmPassword.length > 0) {
      confirmPwdResult = confirmPasswordValidation(confirmPassword, password)
    } else {
      confirmPwdResult.errorMessage = 'This field is required'
    }

    return [createPwdResult, confirmPwdResult]
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (
        values: Values,
        { setSubmitting }: FormikHelpers<Values>
      ) => {
        const [createPwdResult, confirmPwdResult] = handleSubmitPasswordCheck(
          values.createPassword,
          values.confirmPassword
        )

        if (createPwdResult.isValid && confirmPwdResult.isValid) {
          // call api
          console.log('called reset api here')
          const input: ChangePasswordVariables = {
            oldPassword: values.currentPassword,
            newPassword: values.createPassword,
            userName:
              authentication.account && authentication.account.userName
                ? authentication.account.userName
                : ''
          }
          const changePasswordResult = await changePasswordHelper(input)
          if (changePasswordResult.success) {
            nextPage('edit')
          } else {
            if (changePasswordResult.error === 'Incorrect credentials') {
              setCurrentPasswodValidationResult({
                errorMessage:
                  'Your current password is incorrect. Please try again.',
                isValid: false
              })
            } else {
              setCurrentPasswodValidationResult({
                errorMessage: '',
                isValid: true
              })
              setConfirmPasswordValidationResult({
                ...confirmPwdResult,
                errorMessage: 'Reset password had an error. Please try again.',
                confirmPasswordStyle: 'form-control is-invalid'
              })
            }
          }
        } else {
          setSubmitting(false)
          setCreatePasswordValidationResult(createPwdResult)
          setCurrentPasswodValidationResult({
            errorMessage: '',
            isValid: true
          })
          setConfirmPasswordValidationResult({
            ...confirmPwdResult,
            confirmPasswordStyle: 'form-control is-invalid'
          })
        }
      }}
      validationSchema={validationSchema}
    >
      {(formik) => {
        return (
          <Form className='form-container'>
            <div className='row justify-content-md-center'>
              <div className='form-group col-lg-8 col-sm-12 col-md-12'>
                <label htmlFor='currentPassword'>Current Password</label>
                <Field
                  type='password'
                  id='currentPassword'
                  name='currentPassword'
                  className={
                    !currentPasswodValidationResult.isValid
                      ? 'form-control is-invalid'
                      : 'form-control'
                  }
                  placeholder=''
                  autoFocus
                />
                {!currentPasswodValidationResult.isValid && (
                  <div className='text-danger'>
                    {currentPasswodValidationResult.errorMessage}
                  </div>
                )}
                <ErrorMessage
                  name='currentPassword'
                  render={(msg) => <div className='text-danger'>{msg}</div>}
                />
              </div>
            </div>

            <div className='row justify-content-md-center'>
              <div className='form-group col-lg-8 col-sm-12 col-md-12'>
                <label htmlFor='createPassword'>New Password</label>
                <OverlayTrigger
                  trigger='focus'
                  defaultShow={false}
                  placement={placement}
                  overlay={popover}
                >
                  <Field
                    type='password'
                    id='createPassword'
                    name='createPassword'
                    className={
                      formik.errors.createPassword
                        ? 'form-control is-invalid'
                        : createPasswordValidationResult.createPasswordStyle
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      formik.handleChange(e)
                      handlePasswordInput(e.target.value)
                    }}
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handlePasswordInput(e.target.value)
                    }}
                  />
                </OverlayTrigger>
                {createPasswordValidationResult.errorMessages.map(
                  (errorMsg) => (
                    <div className='text-danger' key={errorMsg}>
                      {errorMsg}
                    </div>
                  )
                )}
                <ErrorMessage
                  name='createPassword'
                  render={(msg) => <div className='text-danger'>{msg}</div>}
                />
              </div>
            </div>

            <div className='row justify-content-md-center'>
              <div className='form-group col-lg-8 col-sm-12 col-md-12'>
                <label htmlFor='confirmPassword'>Confirm Password</label>
                <Field
                  type='password'
                  id='confirmPassword'
                  name='confirmPassword'
                  className={
                    formik.errors.createPassword
                      ? 'form-control is-invalid'
                      : confirmPasswordValidationResult.confirmPasswordStyle
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    formik.handleChange(e)
                    setConfirmPasswordValidationResult(confirmPwdInitial)
                  }}
                  placeholder=''
                />
                {confirmPasswordValidationResult.errorMessage && (
                  <div className='text-danger'>
                    {confirmPasswordValidationResult.errorMessage}
                  </div>
                )}
                <ErrorMessage
                  name='confirmPassword'
                  render={(msg) => <div className='text-danger'>{msg}</div>}
                />
              </div>
            </div>

            <div className='row justify-content-md-center'>
              <div className='col-lg-8 col-md-12 col-sm-12'>
                <Button type='submit' className='btn btn-submit w-100'>
                  Reset Password
                </Button>
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default UserProfileResetPasswordForm
